/**
 * SalesRepSelector Component
 *
 * Allows providers to select a sales rep for manual meeting entry:
 * 1. Search RepConnect database for registered sales reps
 * 2. Manually enter sales rep info (name, company, email required)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Search, User, Building, Mail, Phone, AlertCircle, CheckCircle, UserPlus, X } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { logger } from '../utils/logger';

const SalesRepSelector = ({
  selectedRep,           // Current selected rep object or null
  onRepSelect,           // Callback with rep object { id, name, company, email, isExternal }
  onRepClear             // Callback to clear selection
}) => {
  const [mode, setMode] = useState('search'); // 'search' or 'manual'

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Manual entry state
  const [manualRep, setManualRep] = useState({
    name: '',
    company: '',
    email: '',
    phone: ''
  });
  const [emailError, setEmailError] = useState('');

  // Search for sales reps in database
  const searchSalesReps = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Query user_profiles for sales reps
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, company, phone, profile_picture')
        .eq('user_type', 'sales')
        .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,company.ilike.%${query}%,email.ilike.%${query}%`)
        .limit(10);

      if (!error && data) {
        const formattedResults = data.map(rep => ({
          id: rep.id,
          name: `${rep.first_name || ''} ${rep.last_name || ''}`.trim() || 'Unknown',
          company: rep.company || '',
          email: rep.email || '',
          phone: rep.phone || '',
          profilePicture: rep.profile_picture,
          isExternal: false
        }));
        setSearchResults(formattedResults);
      } else if (error) {
        logger.error('Error searching sales reps', { error: error.message });
        setSearchResults([]);
      }
    } catch (error) {
      logger.error('Error searching sales reps', { error: error.message });
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (mode === 'search') {
        searchSalesReps(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, mode, searchSalesReps]);

  // Validate email
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Handle manual entry email change
  const handleEmailChange = (email) => {
    setManualRep(prev => ({ ...prev, email }));
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  // Confirm manual entry
  const confirmManualEntry = () => {
    if (!manualRep.name.trim() || !manualRep.email.trim()) {
      return;
    }
    if (!validateEmail(manualRep.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    onRepSelect({
      id: null,  // null indicates external rep
      name: manualRep.name.trim(),
      company: manualRep.company.trim(),
      email: manualRep.email.trim(),
      phone: manualRep.phone.trim(),
      isExternal: true
    });
  };

  // Reset manual form when switching modes
  useEffect(() => {
    if (mode === 'manual') {
      setSearchQuery('');
      setSearchResults([]);
    } else {
      setManualRep({ name: '', company: '', email: '', phone: '' });
      setEmailError('');
    }
  }, [mode]);

  // If rep is already selected, show summary
  if (selectedRep) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{selectedRep.name}</p>
              {selectedRep.company && (
                <p className="text-sm text-gray-600">{selectedRep.company}</p>
              )}
              <p className="text-sm text-gray-500">{selectedRep.email}</p>
              {selectedRep.phone && (
                <p className="text-sm text-gray-500">{selectedRep.phone}</p>
              )}
              {selectedRep.isExternal && (
                <span className="inline-flex items-center px-2 py-0.5 mt-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                  External Rep (not on RepConnect)
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onRepClear}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50">
        <button
          onClick={() => setMode('search')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            mode === 'search'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Search className="w-4 h-4 inline mr-2" />
          Search RepConnect
        </button>
        <button
          onClick={() => setMode('manual')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            mode === 'manual'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <UserPlus className="w-4 h-4 inline mr-2" />
          Enter Manually
        </button>
      </div>

      {mode === 'search' ? (
        <div className="space-y-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, company, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Search Results */}
          {isSearching && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Searching...</p>
            </div>
          )}

          {!isSearching && searchResults.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {searchResults.map((rep) => (
                <button
                  key={rep.id}
                  onClick={() => onRepSelect(rep)}
                  className="w-full p-3 text-left bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {rep.profilePicture ? (
                      <img
                        src={rep.profilePicture}
                        alt={rep.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{rep.name}</p>
                      {rep.company && <p className="text-sm text-gray-600 truncate">{rep.company}</p>}
                      <p className="text-sm text-gray-500 truncate">{rep.email}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!isSearching && searchQuery.length >= 2 && searchResults.length === 0 && (
            <div className="text-center py-6 bg-gray-50 rounded-lg">
              <User className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">No sales reps found</p>
              <button
                onClick={() => setMode('manual')}
                className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Enter rep details manually
              </button>
            </div>
          )}

          {searchQuery.length < 2 && searchQuery.length > 0 && (
            <p className="text-sm text-gray-500 text-center py-2">
              Type at least 2 characters to search
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4 bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-3">
            Enter the sales rep's information. They will receive an email notification about this meeting.
          </p>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rep Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="John Smith"
                value={manualRep.name}
                onChange={(e) => setManualRep(prev => ({ ...prev, name: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Pharma Corp"
                value={manualRep.company}
                onChange={(e) => setManualRep(prev => ({ ...prev, company: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                placeholder="john@company.com"
                value={manualRep.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${
                  emailError
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              />
            </div>
            {emailError && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {emailError}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                placeholder="(555) 555-5555"
                value={manualRep.phone}
                onChange={(e) => setManualRep(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <button
            onClick={confirmManualEntry}
            disabled={!manualRep.name.trim() || !manualRep.email.trim() || !!emailError}
            className="w-full py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm Sales Rep
          </button>

          <p className="text-xs text-gray-500 text-center">
            The sales rep will receive an email notification about this meeting and an invitation to join RepConnect.
          </p>
        </div>
      )}
    </div>
  );
};

export default SalesRepSelector;
